import React from 'react';
import PinyinGrid from './PinyinGrid';

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;

function getCharWidthUnit(char) {
  if (char.isSpace) return 0.35;
  if (char.isHanzi) return 1;
  return 0.8;
}

const GridRenderer = ({ characters, settings, titleRows = [], worksheetTitle = '', showPageNumbers = false, onPinyinSelect }) => {
  if (!characters || characters.length === 0) {
    return null;
  }

  const {
    cellSize = 40,
    pinyinHeight = 20,
    colsPerRow = 12,
    rowGap = 0,
    marginTop = 60,
    marginBottom = 60,
    marginLeft = 40,
    marginRight = 40,
  } = settings;

  const lineGap = rowGap || 0;
  const rowHeight = settings.showPinyin ? cellSize + pinyinHeight : cellSize;
  const titleRowHeight = 30;
  const worksheetTitleHeight = worksheetTitle ? 36 : 0;
  const pageNumberHeight = showPageNumbers ? 22 : 0;
  const availableHeight = PAGE_HEIGHT - marginTop - marginBottom - pageNumberHeight;

  // 按换行符和每行格子数分割成多行
  const lines = [];
  let currentLine = [];
  let widthUnitsInLine = 0;

  characters.forEach((char) => {
    if (char.isLineBreak) {
      if (currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = [];
        widthUnitsInLine = 0;
      } else {
        lines.push([]);
      }
    } else {
      const charUnit = getCharWidthUnit(char);
      if (currentLine.length > 0 && widthUnitsInLine + charUnit > colsPerRow) {
        lines.push(currentLine);
        currentLine = [];
        widthUnitsInLine = 0;
      }

      currentLine.push(char);
      widthUnitsInLine += charUnit;

      if (widthUnitsInLine >= colsPerRow) {
        lines.push(currentLine);
        currentLine = [];
        widthUnitsInLine = 0;
      }
    }
  });

  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  const normalizedTitleRows = Array.isArray(titleRows) ? titleRows : [];
  const contentRows = [];
  if (worksheetTitle) {
    contentRows.push({ type: 'worksheet-title', title: worksheetTitle });
  }
  lines.forEach((line, lineIndex) => {
    normalizedTitleRows
      .filter(item => item.lineIndex === lineIndex && item.title)
      .forEach(item => contentRows.push({ type: 'title', title: item.title }));
    contentRows.push({ type: 'line', line });
  });
  normalizedTitleRows
    .filter(item => item.lineIndex >= lines.length && item.title)
    .forEach(item => contentRows.push({ type: 'title', title: item.title }));

  const pages = [];
  let currentPage = [];
  let usedHeight = 0;

  contentRows.forEach((row, index) => {
    const rowHeightPx = row.type === 'worksheet-title' ? worksheetTitleHeight : row.type === 'title' ? titleRowHeight : rowHeight;
    const nextRow = contentRows[index + 1];
    const nextRowHeight = nextRow
      ? nextRow.type === 'worksheet-title'
        ? worksheetTitleHeight
        : nextRow.type === 'title'
          ? titleRowHeight
          : rowHeight
      : 0;
    const shouldAvoidOrphanTitle = row.type === 'title'
      && nextRow
      && currentPage.length > 0
      && usedHeight + lineGap + rowHeightPx + lineGap + nextRowHeight > availableHeight;

    if (shouldAvoidOrphanTitle) {
      pages.push(currentPage);
      currentPage = [];
      usedHeight = 0;
    }

    const nextHeight = usedHeight + (currentPage.length > 0 ? lineGap : 0) + rowHeightPx;

    if (currentPage.length > 0 && nextHeight > availableHeight) {
      pages.push(currentPage);
      currentPage = [];
      usedHeight = 0;
    }

    currentPage.push(row);
    usedHeight += (currentPage.length > 1 ? lineGap : 0) + rowHeightPx;
  });

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return (
    <>
      {pages.map((pageRows, pageIndex) => (
        <div
          key={`page-${pageIndex}`}
          className="paper-preview"
          style={{
            width: `${PAGE_WIDTH}px`,
            height: `${PAGE_HEIGHT}px`,
            paddingLeft: `${marginLeft}px`,
            paddingRight: `${marginRight}px`,
            paddingTop: `${marginTop}px`,
            paddingBottom: `${marginBottom}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${lineGap}px`,
          }}
        >
          {pageRows.map((row, rowIndex) => {
            if (row.type === 'worksheet-title') {
              return (
                <div
                  key={`${pageIndex}-worksheet-title-${rowIndex}`}
                  style={{
                    height: `${worksheetTitleHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'KaiTi, STKaiti, serif',
                    fontSize: '20px',
                    fontWeight: 600,
                    color: settings.hanziColor || '#333',
                    letterSpacing: '1px',
                    flexShrink: 0,
                  }}
                >
                  {row.title}
                </div>
              );
            }

            if (row.type === 'title') {
              return (
                <div
                  key={`${pageIndex}-title-${rowIndex}`}
                  style={{
                    height: `${titleRowHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'KaiTi, STKaiti, serif',
                    fontSize: '17px',
                    color: settings.hanziColor || '#333',
                    letterSpacing: '1px',
                    flexShrink: 0,
                  }}
                >
                  {row.title}
                </div>
              );
            }

            const line = row.line;
            return (
              <div
                key={`${pageIndex}-${rowIndex}`}
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'flex-end',
                  gap: '0px',
                  minHeight: `${rowHeight}px`,
                }}
              >
                {line.map((char, charIndex) => {
                if (char.isHanzi) {
                  return (
                    <PinyinGrid
                      key={`hanzi-${char.index}-${charIndex}`}
                      character={char}
                      settings={settings}
                      index={characters.indexOf(char)}
                      onPinyinSelect={onPinyinSelect}
                    />
                  );
                } else if (char.isSpace) {
                  return (
                    <div
                      key={`space-${char.index}-${charIndex}`}
                      style={{ width: cellSize / 3, flexShrink: 0 }}
                    />
                  );
                } else {
                  const totalH = settings.showPinyin ? cellSize + pinyinHeight : cellSize;
                  return (
                    <div
                      key={`punct-${char.index}-${charIndex}`}
                      style={{
                        width: cellSize,
                        height: totalH,
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        paddingBottom: cellSize * 0.15,
                        flexShrink: 0,
                      }}
                    >
                      <span style={{
                        fontSize: cellSize * 0.5,
                        color: settings.hanziColor || '#333',
                        fontFamily: 'KaiTi, STKaiti, serif',
                      }}>
                        {char.char}
                      </span>
                    </div>
                  );
                }
                })}
              </div>
            );
          })}
          {showPageNumbers && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: '16px',
                textAlign: 'center',
                fontSize: '11px',
                color: '#999',
                fontFamily: 'Arial, sans-serif',
              }}
            >
              第 {pageIndex + 1} 页 / 共 {pages.length} 页
            </div>
          )}
        </div>
      ))}
    </>
  );
};

export default GridRenderer;
